import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import {compose} from "mu-lambda";
import * as React from "react";

import {DateField, IntegerField} from "@khanacademy/event-schemas";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {RequestInfo} from "@repo/shared-core";
import {
    mediaQueries,
    topBarHeightDesktop,
    topBarHeightMobile,
} from "@repo/shared-styles";

import type {
    DCULPath,
    NewAssignmentSubmitParameters,
} from "#assignments-package/types.ts";
import {dateOffsetFromDefaultDueDate} from "#assignments-package/util.tsx";
import withApolloQuery, {
    type WithoutApolloQuery,
} from "#data-access/gql/with-apollo-query.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";

/**
 * We need to be smart here and only create a new Loadable once rather than
 * every render. So let's cache the first one we make then reuse it.
 */
const CreateAssignmentModalComponent = React.lazy(
    () => import("../assignments-package/create-assignment-modal-provider.tsx"),
);

type Data = {
    domains?: Array<{
        subjects: Array<{
            id: string;
            slug: string;
        }>;
    }>;
};

type MappedProps = {
    subjectSlugs: Array<string>;
};

type Props = {
    contentDescriptors: string[];
    classroomDescriptor: string;
    studentKaids: Array<string>;
    onSuccess?: () => unknown;
    onClose?: () => unknown;
    contentPath?: DCULPath;
    initialAssignment?: any;
} & WithLocationProps &
    WithUserProfileProps &
    MappedProps;

// NOTE(jeresig, CLASS-6163): It turns out that this isn't needed any more, we
// can get the current course directly from the URL, so let's remove this
// query!
const DATA_QUERY: GqlOperation<Data> = gqlOp(gql`
    query getAllSubject($region: String!) {
        domains: studentListTopics(region: $region) {
            subjects: topics {
                id
                slug
            }
        }
    }
`);

/*
 * This component is used to asynchronously load the Assignment Modal with specific Content Descriptors and Classroom Descriptor / Student Kaids used for preselecting the Assignees.
 *
 * The component hierarchy between this and AssignmentModal is as follows:
 *
 * 1. `AsyncCreateAssignmentModal`: Injects a dynamic import of `CreateAssignmentModalProvider` into  `LoadableCreateAssignmentModalComponent ` using the `LoadableComponent` from the `react-loadable` package. Has props `contentDescriptors`, `classroomDescriptor`, and `studentKaids`. These will be passed down through each level of the component hierarchy.
 * 2. `CreateAssignmentModalProvider`:  A simple component that renders two other child components. It wraps the `CreateAssignmentModalLauncher` component with the `StoreProvider` component from the package `class-package`. This wrapping of `CreateAssignmentModalLauncher` with `StoreProvider` component is required so the that the `CreateAssignmentModalLauncher` can start to set up the specific redux store that `CreateAssignmentModalLauncher` from `classroom-package` package offers.
 * 3. `CreateAssignmentModalLauncher`: The `CreateAssignmentModalLauncher` component calls `createStore` to instantiate a redux store provided by parent `StoreProvider` component and dispatches the `newAssignment` action containing the inherited props `contentDescriptors`, `classroomDescriptor`, and `studentKaids`.  WIth the redux store all set up and containing the context of the new assignment, `CreateAssignmentModalLauncher` renders `AssignmentModal` wrapped with the `ModalLauncher` component from Wonder Blocks. Because `ModalLauncher`'s `opened` prop is hardcoded to `true`, it opens right away. At this point the teacher should see the Assignment Modal with the classroom, students, and content passed through by props now preselected in the Assignments Modal.
 *
 */
const AsyncCreateAssignmentModal = ({
    contentDescriptors,
    classroomDescriptor,
    studentKaids,
    onSuccess,
    onClose,
}: Props): React.ReactElement => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const handleAssignRecommendationsSubmit = (
        params: NewAssignmentSubmitParameters,
    ) => {
        const {
            assignment,
            qualifiedContentDescriptors,
            numStudentsInClass,
            createdAssignments,
        } = params;

        for (const contentDescriptor of qualifiedContentDescriptors) {
            const parts =
                contentDescriptor !== "" ? contentDescriptor.split(":") : [];
            const domainId = parts[0] ?? null;
            const courseId = parts[1] ?? null;
            const unitId = parts[2] ?? null;
            const lessonId = parts[3] ?? null;
            const contentKind = parts[4] ?? null;
            const contentId = parts[5] ?? null;

            // Find the corresponding created assignment id for the
            // recommendation content descriptor
            let assignmentID: string | null = null;
            if (createdAssignments) {
                for (const createdAssignment of createdAssignments) {
                    if (
                        createdAssignment.contents &&
                        createdAssignment.contents.length > 0
                    ) {
                        const createdAssignmentContentKind =
                            createdAssignment.contents[0].kind;
                        const createdAssignmentContentId =
                            createdAssignment.contents[0].id ?? "";

                        if (
                            contentKind === createdAssignmentContentKind &&
                            contentId === createdAssignmentContentId
                        ) {
                            assignmentID = createdAssignment.id ?? null;
                            break;
                        }
                    }
                }
            }

            publishAnalyticsEvent({
                eventType: "AiGuideTeacherRecContentAssignmentSubmitted",
                eventSchemaVersion: 4,
                assignmentId: assignmentID,
                classroomDescriptor: classroomDescriptor || "",
                scheduledStartDate: new DateField(assignment.startDate),
                dueDateTime: new DateField(assignment.dueDate),
                numAssignees: new IntegerField(assignment.students.length),
                numRecommendedAssignees: new IntegerField(
                    studentKaids.length || 0,
                ),
                assignedStudentListMatchedRecommendation:
                    assignment.students.map((s) => s.kaid) === studentKaids,
                sameQuestionSetForAllStudents:
                    assignment.questionSetType === undefined
                        ? "RANDOM"
                        : assignment.questionSetType === "RANDOM"
                          ? "RANDOM"
                          : assignment.questionSetType === "SAME"
                            ? "SAME"
                            : "RANDOM",
                isDraft: assignment.isDraft,
                assignmentContentPath_contentKind:
                    contentKind === "Article"
                        ? "Article"
                        : contentKind === "Video"
                          ? "Video"
                          : contentKind === "Exercise"
                            ? "Exercise"
                            : contentKind === "Quiz"
                              ? "Quiz"
                              : contentKind === "UnitTest"
                                ? "UnitTest"
                                : "CourseChallenge",
                assignmentContentPath_domainId: domainId,
                assignmentContentPath_courseId: courseId,
                assignmentContentPath_unitId: unitId,
                numStudentsInClass: new IntegerField(numStudentsInClass),
                isAssignedToEntireClass:
                    assignment.students.length === numStudentsInClass,
                numDaysSpecifiedDueDateIsOffsetFromDefault: new IntegerField(
                    dateOffsetFromDefaultDueDate(assignment.dueDate),
                ),
                assignmentContentPath_kaLocale: RequestInfo.kaLocale,
                assignmentContentPath_lessonId: lessonId,
                assignmentContentPath_contentId: contentId,
            });
        }
    };

    return (
        <React.Suspense
            fallback={
                <div className={css(styles.assignmentLoadingIndicator)}>
                    <DeprecatedSpinner size="xsmall" light={true} />
                </div>
            }
        >
            <CreateAssignmentModalComponent
                contentDescriptors={contentDescriptors}
                classroomDescriptor={classroomDescriptor}
                studentKaids={studentKaids}
                onSuccess={onSuccess}
                onClose={onClose}
                onSubmit={handleAssignRecommendationsSubmit}
            />
        </React.Suspense>
    );
};

const styles = StyleSheet.create({
    assignmentLoadingIndicator: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: topBarHeightDesktop,
        backgroundColor: color.darkBlue,

        [mediaQueries.smOrSmaller]: {
            height: topBarHeightMobile,
        },
    },
});

type ExportProps = WithoutLocation<
    WithoutUserProfile<
        WithoutApolloQuery<
            JSX.LibraryManagedAttributes<
                typeof AsyncCreateAssignmentModal,
                React.ComponentProps<typeof AsyncCreateAssignmentModal>
            >,
            MappedProps
        >
    >
>;

export default compose(
    withLocation,
    withUserProfile,
    withApolloQuery({
        query: DATA_QUERY,
        fetchPolicy: "cache-first",
        variables: {
            region: RequestInfo.kaRegion,
        },
        mapResultToProps: ({data}) => {
            const subjectSlugs: Array<string> = [];

            if (!data?.domains) {
                return {subjectSlugs};
            }

            for (const domain of data.domains) {
                for (const subject of domain.subjects) {
                    subjectSlugs.push(subject.slug);
                }
            }
            return {subjectSlugs};
        },
    }),
)(AsyncCreateAssignmentModal) as React.ComponentType<ExportProps>;
